/**
 * Tileable value noise, shared by everything that draws a ground swatch arithmetically.
 *
 * Two callers exist and they run in different worlds: {@link WinterGround} draws snow and ice in the
 * browser at the moment the designer switches winter on, and `sites/swatchforge.mjs` draws paving and
 * mulch in Node when a site file is generated. They want the same lattice — a swatch that does not
 * wrap shows as a visible grid on the ground at exactly the distance the anisotropic filtering is
 * there to rescue — so the lattice lives here rather than being written twice and drifting.
 *
 * Everything is tileable by construction: the lattice frequency is an integer and the hash is taken
 * on the *wrapped* lattice point, so `noise(0, v)` and `noise(1, v)` read the same corner. A shear
 * layer stays tileable as long as it shifts by a whole number of periods across the tile.
 *
 * Deterministic from the seed down: no clock, no `Math.random`. A generator has to produce the same
 * bytes on every run, and a texture the designer toggles off and on again must not come back
 * different.
 */

/** Integer hash on a wrapped lattice point, in [0, 1). */
export function hash2(ix: number, iy: number, seed: number): number {
  let h = Math.imul(ix | 0, 0x27d4eb2d) ^ Math.imul(iy | 0, 0x165667b1) ^ Math.imul(seed | 0, 0x9e3779b1);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

export const fract = (v: number): number => v - Math.floor(v);
export const wrap = (v: number, n: number): number => ((v % n) + n) % n;
export const fade = (t: number): number => t * t * (3 - 2 * t);
export const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
export const smooth01 = (t: number): number => fade(clamp01(t));
export const mix = (a: number, b: number, t: number): number => a + (b - a) * t;

/**
 * Value noise on an `fx × fy` lattice. Separate periods per axis so a material can be stretched —
 * bark strands are long in one direction and thin in the other, and that is the whole look.
 */
export function noise(u: number, v: number, fx: number, fy: number, seed: number): number {
  const x = fract(u) * fx;
  const y = fract(v) * fy;
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const tx = fade(x - x0);
  const ty = fade(y - y0);
  const xa = wrap(x0, fx);
  const xb = wrap(x0 + 1, fx);
  const ya = wrap(y0, fy);
  const yb = wrap(y0 + 1, fy);
  const top = mix(hash2(xa, ya, seed), hash2(xb, ya, seed), tx);
  const bottom = mix(hash2(xa, yb, seed), hash2(xb, yb, seed), tx);
  return mix(top, bottom, ty);
}

/**
 * Octaves of {@link noise}, each doubling the lattice. Roughly [0, 1] with a mean of 0.5.
 *
 * Callers keep the top octave at or below half the swatch resolution — i.e. features of four texels
 * or more. Per-texel white noise reads as grain on screen, roughly triples what a PNG of the swatch
 * costs, and the mip chain throws it away by the second level regardless.
 */
export function fbm(u: number, v: number, f: number, octaves: number, seed: number): number {
  let sum = 0;
  let amp = 1;
  let norm = 0;
  let freq = f;
  for (let o = 0; o < octaves; o += 1) {
    sum += noise(u, v, freq, freq, seed + o * 101) * amp;
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  return sum / norm;
}
